import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query'
import { scanTicket } from 'api/ticket'
import { ConfirmButton, ConfirmButtonLarge } from 'components/button/button.styled'
import { Html5Qrcode } from 'html5-qrcode'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { EventDetails } from 'types/Event'

import { ScanMessage, ScanMessageWrapper } from '../ticket.style'
import { CurrentlyScannedTicket } from './currentlyScannedTicket'
import { Container, ScannerContainer, ScannerWrapper } from './ticketScan.style'

interface Props {
    event: EventDetails
    refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<EventDetails, Error>>
}

const QR_ELEMENT_ID = 'qr-reader'

export const TicketScan = ({ event, refetch }: Props) => {
    const navigate = useNavigate()
    const scannerRef = useRef<Html5Qrcode | null>(null)
    const hasScannedRef = useRef(false)

    const [scanResult, setScanResult] = useState<{
        success: boolean
        seatData: any
    } | null>(null)

    const [errorMessage, setErrorMessage] = useState<string | null>(null)

    const stopScanner = async () => {
        if (scannerRef.current) {
            try {
                await scannerRef.current.stop()
            } catch {
                // already stopped
            }
            scannerRef.current = null
        }
    }

    const handleScan = useCallback(
        async (scannedTicketId: string) => {
            if (hasScannedRef.current) return
            if (!event?.event_id) {
                setErrorMessage('Az esemény adatai nem érhetők el. Kérjük, frissítse az oldalt.')
                return
            }
            hasScannedRef.current = true
            await stopScanner()

            try {
                const { success, seatData } = await scanTicket(scannedTicketId, event.event_id)
                setScanResult({ success, seatData })
                refetch()
                setErrorMessage(null)
            } catch (err: any) {
                console.error('Beolvasás sikertelen:', err.message)
                setErrorMessage(err.message)
                hasScannedRef.current = false
            }
        },
        [event.event_id, refetch],
    )

    useEffect(() => {
        if (scanResult || errorMessage) return

        const scanner = new Html5Qrcode(QR_ELEMENT_ID)
        scannerRef.current = scanner

        scanner
            .start({ facingMode: 'environment' }, { fps: 10 }, (decodedText: string) => handleScan(decodedText), undefined)
            .catch((err: unknown) => console.error('Camera start failed:', err))

        return () => {
            stopScanner()
        }
    }, [scanResult, errorMessage, handleScan])

    const resetScanner = () => {
        hasScannedRef.current = false
        setScanResult(null)
        setErrorMessage(null)
    }

    if (scanResult) {
        return (
            <CurrentlyScannedTicket
                event={event}
                currentSeatData={scanResult.seatData}
                success={scanResult.success}
                onScanNewTicket={resetScanner}
            />
        )
    }

    if (errorMessage) {
        return (
            <ScanMessageWrapper>
                <ScanMessage success={false}>{errorMessage}</ScanMessage>
                <ConfirmButtonLarge onClick={resetScanner}>Új jegy beolvasása</ConfirmButtonLarge>
            </ScanMessageWrapper>
        )
    }

    return (
        <Container>
            <ConfirmButton onClick={() => navigate('/admin/jegyellenorzes/telitettseg')}>Nézőtér telítettsége</ConfirmButton>
            <ScannerWrapper>
                <ScannerContainer>
                    <div id={QR_ELEMENT_ID} style={{ width: '100%', height: '100%' }} />
                </ScannerContainer>
            </ScannerWrapper>
        </Container>
    )
}
